import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Activity,
  Database,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { EntityTable, SyncEnvironment, EntityData, EntityMetadataPreview } from "@/components/sql-sync/EntityTable";
import { SyncDurationChart } from "@/components/sql-sync/SyncDurationChart";
import { usePermissions } from "@/contexts/PermissionContext";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import {
  fetchSqlSyncDashboard,
  fetchSqlSyncFailedRecords,
  fetchSqlSyncMetadata,
  refreshSqlSyncMetadata,
  startSqlSync,
  resumeSqlSync,
  stopSqlSync,
  detectSqlSyncChanges,
  resetAndRescanSqlSync,
  startFieldSync,
  flagAllForFieldSync,
  SqlSyncDashboardResponse,
  SqlSyncDashboardEntity,
  SqlSyncFailedRecord,
  SqlSyncEntityMetadata,
} from "@/lib/api";

// ============================================================================
// Helper Functions
// ============================================================================

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return "Never";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  } catch {
    return "Error";
  }
};

// Single-flag architecture: environment is fixed (no per-record tracking)
const DEFAULT_ENVIRONMENT: SyncEnvironment = "Production";

// ============================================================================
// Failed Record Row Component
// ============================================================================

const FailedRecordRow = ({ record }: { record: SqlSyncFailedRecord }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(record.payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <TableRow className="hover:bg-muted/50 cursor-pointer">
        <TableCell className="w-10">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </TableCell>
        <TableCell className="w-28 font-mono text-sm">{record.entityKey}</TableCell>
        <TableCell className="truncate text-sm" title={record.errorMessage}>
          {record.errorMessage}
        </TableCell>
        <TableCell className="w-44 text-sm text-muted-foreground whitespace-nowrap">
          {formatDate(record.failedAt)}
        </TableCell>
        <TableCell className="w-12">
          <Button variant="ghost" size="sm" onClick={copyPayload} title="Copy payload">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </TableCell>
      </TableRow>
      <CollapsibleContent asChild>
        <TableRow>
          <TableCell colSpan={5} className="p-0">
            <div className="bg-muted/30 border-t border-b">
              <div className="p-3 border-b bg-red-50">
                <div className="text-xs font-medium text-red-700 mb-1">Error Message:</div>
                <div className="text-sm text-red-800 whitespace-pre-wrap break-words">
                  {record.errorMessage}
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Payload:</span>
                  <Button variant="outline" size="sm" onClick={copyPayload} className="h-7">
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 mr-1 text-green-600" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" /> Copy JSON
                      </>
                    )}
                  </Button>
                </div>
                <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-64 font-mono">
                  {JSON.stringify(record.payload, null, 2)}
                </pre>
              </div>
            </div>
          </TableCell>
        </TableRow>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ============================================================================
// Failed Records Drawer Component
// ============================================================================

const FailedRecordsDrawer = ({
  isOpen,
  onClose,
  entityType,
}: {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["sqlSyncFailedRecords", entityType],
    queryFn: () => fetchSqlSyncFailedRecords(entityType, DEFAULT_ENVIRONMENT, 50),
    enabled: isOpen,
  });

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-5xl w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            Failed Records: {entityType}
          </SheetTitle>
          <SheetDescription>
            {data?.totalFailed ? `${data.totalFailed} total failed records` : "Loading..."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 rounded-lg text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>Failed to load failed records: {(error as Error).message}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
                <RefreshCw className="h-4 w-4 mr-2" /> Retry
              </Button>
            </div>
          )}

          {data?.records && data.records.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <p>No failed records</p>
            </div>
          )}

          {data?.records && data.records.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="w-28">Entity Key</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="w-44">Failed At</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.records.map((record) => (
                    <FailedRecordRow key={record.id} record={record} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

export default function SqlSyncPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { hasPermission } = usePermissions();
  const canExecute = hasPermission("scheduler", "execute");
  const executeMessage = "You don't have permission to execute sync actions";
  // Single-flag architecture: use fixed environment
  const environment = DEFAULT_ENVIRONMENT;
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [errorDrawerEntity, setErrorDrawerEntity] = useState<string | null>(null);
  const [maxRecords, setMaxRecords] = useState<number>(0);
  const [actioningEntity, setActioningEntity] = useState<string | null>(null);

  // Track actions per entity
  const [scanningEntities, setScanningEntities] = useState<Set<string>>(new Set());
  const [startingEntities, setStartingEntities] = useState<Set<string>>(new Set());
  const [stoppingEntities, setStoppingEntities] = useState<Set<string>>(new Set());
  const [resumingEntities, setResumingEntities] = useState<Set<string>>(new Set());
  const [resettingEntities, setResettingEntities] = useState<Set<string>>(new Set());
  const [retryingFailedEntities, setRetryingFailedEntities] = useState<Set<string>>(new Set());
  // Field sync tracking
  const [flaggingFieldSyncEntities, setFlaggingFieldSyncEntities] = useState<Set<string>>(new Set());
  const [fieldSyncingEntities, setFieldSyncingEntities] = useState<Set<string>>(new Set());

  // Fetch consolidated dashboard data (replaces multiple queries)
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ["sqlSyncDashboard"],
    queryFn: fetchSqlSyncDashboard,
    staleTime: 10000, // 10 seconds client-side
    refetchInterval: autoRefresh ? 15000 : false, // 15s polling (server caches for 15s)
  });

  // Fetch entity sync metadata (cached counts from database)
  const {
    data: metadataData,
    isLoading: metadataLoading,
    refetch: refetchMetadata,
  } = useQuery({
    queryKey: ["sqlSyncMetadata"],
    queryFn: fetchSqlSyncMetadata,
    staleTime: 60000, // 1 minute client-side (data is from scheduled 5-min refresh)
    refetchInterval: autoRefresh ? 60000 : false, // 1 min polling
  });

  // Build a lookup map from entityType to metadata
  const metadataByEntity = new Map<string, SqlSyncEntityMetadata>();
  metadataData?.metadata?.forEach(m => metadataByEntity.set(m.entityType, m));

  // Refresh metadata mutation (manual refresh button)
  const refreshMetadataMutation = useMutation({
    mutationFn: () => refreshSqlSyncMetadata(),
    onSuccess: (data) => {
      if (data.success) {
        const successCount = data.results.filter(r => r.success).length;
        toast({
          title: "Metadata Refreshed",
          description: `Updated counts for ${successCount} entities in ${(data.totalDurationMs / 1000).toFixed(1)}s`,
        });
        refetchMetadata();
        refetchDashboard();
      } else {
        toast({
          title: "Refresh Failed",
          description: data.error || "Failed to refresh metadata",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Filter to only enabled entities
  const enabledEntities = dashboardData?.entities?.filter(e => e.enabled) || [];

  // Start sync mutation
  const startMutation = useMutation({
    mutationFn: ({ entityType, env, limit }: { entityType: string; env: SyncEnvironment; limit?: number }) =>
      startSqlSync(entityType, env, limit),
    onMutate: ({ entityType }) => {
      setActioningEntity(entityType);
      setStartingEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, variables) => {
      if (data.success && data.jobId) {
        toast({
          title: "Sync Started",
          description: `Job ${data.jobId} started successfully`,
        });
        refetchDashboard();
      } else if (data.canCancel && data.runningJobId) {
        toast({
          title: "Sync Already Running",
          description: `${variables.entityType} is locked by job ${data.runningJobId}. Cancel it if it is stale.`,
          variant: "destructive",
          action: (
            <ToastAction
              altText={`Cancel stale ${variables.entityType} sync`}
              onClick={() => stopMutation.mutate({ entityType: variables.entityType, env: variables.env })}
            >
              Cancel stale job
            </ToastAction>
          ),
        });
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to start sync",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, { entityType }) => {
      setActioningEntity(null);
      setStartingEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Resume sync mutation
  const resumeMutation = useMutation({
    mutationFn: ({ entityType, env, limit }: { entityType: string; env: SyncEnvironment; limit?: number }) =>
      resumeSqlSync(entityType, env, limit),
    onMutate: ({ entityType }) => {
      setActioningEntity(entityType);
      setResumingEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Sync Resumed",
          description: `Resuming ${data.pendingCount?.toLocaleString() || 0} pending records`,
        });
        refetchDashboard();
      } else {
        toast({ title: "Error", description: data.error || "Failed to resume", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, { entityType }) => {
      setActioningEntity(null);
      setResumingEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Stop sync mutation
  const stopMutation = useMutation({
    mutationFn: ({ entityType, env }: { entityType: string; env: SyncEnvironment }) =>
      stopSqlSync(entityType, env),
    onMutate: ({ entityType }) => {
      setActioningEntity(entityType);
      setStoppingEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: data.status === "cancelled" ? "Sync Stopped" : "Stop Requested",
          description: data.message || "Sync has been stopped",
        });
        refetchDashboard();
      } else {
        toast({ title: "Error", description: data.error || "Failed to stop", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, { entityType }) => {
      setActioningEntity(null);
      setStoppingEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Detect changes mutation (now queues a background task)
  const detectChangesMutation = useMutation({
    mutationFn: ({ entityType, resetFailedFlag }: { entityType: string; resetFailedFlag: boolean }) =>
      detectSqlSyncChanges(entityType, environment, false, resetFailedFlag),
    onMutate: ({ entityType }) => {
      setActioningEntity(entityType);
      setScanningEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, { entityType }) => {
      if (data.success) {
        if (data.queued) {
          // Task was queued for background processing
          toast({
            title: `Scan Started: ${entityType}`,
            description: `Change detection is running in the background. Progress will update automatically.`,
          });
        } else {
          // Legacy synchronous response (shouldn't happen with new API)
          const { totalChanges = 0, newRecords = 0, updatedRecords = 0, deletedRecords = 0, durationMs = 0 } = data;
          if (totalChanges > 0) {
            toast({
              title: `Changes Detected: ${entityType}`,
              description: `Found ${totalChanges.toLocaleString()} changes (${newRecords} new, ${updatedRecords} updated, ${deletedRecords} deleted) in ${(durationMs / 1000).toFixed(1)}s`,
            });
          } else {
            toast({
              title: `No Changes: ${entityType}`,
              description: `No new changes detected (scan took ${(durationMs / 1000).toFixed(1)}s)`,
            });
          }
        }
        refetchDashboard();
      } else {
        toast({ title: "Error", description: data.error || "Failed to detect changes", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, { entityType }) => {
      setActioningEntity(null);
      setScanningEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Queue All mutation - marks all records as pending for re-sync (keeps existing payloads)
  const resetAndRescanMutation = useMutation({
    mutationFn: (entityType: string) => resetAndRescanSqlSync(entityType, environment, false),
    onMutate: (entityType) => {
      setActioningEntity(entityType);
      setResettingEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, entityType) => {
      if (data.success) {
        if (data.queued) {
          toast({
            title: `Full Rescan Started: ${entityType}`,
            description: `Rebuilding staging from source in the background. Run SQL Sync after the rescan completes.`,
          });
        } else {
          const { totalChanges = 0, durationMs = 0 } = data;
          toast({
            title: `Full Rescan Complete: ${entityType}`,
            description: `Rebuilt staging with ${totalChanges.toLocaleString()} detected changes in ${(durationMs / 1000).toFixed(1)}s. Run SQL Sync to send them to Dataverse.`,
          });
        }
        refetchDashboard();
      } else {
        toast({ title: "Error", description: data.error || "Failed to queue records", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, entityType) => {
      setActioningEntity(null);
      setResettingEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Retry Failed mutation - resets only failed records back to pending
  const retryFailedMutation = useMutation({
    mutationFn: (entityType: string) => detectSqlSyncChanges(entityType, environment, false, true),
    onMutate: (entityType) => {
      setActioningEntity(entityType);
      setRetryingFailedEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, entityType) => {
      if (data.success) {
        toast({
          title: `Retry Failed: ${entityType}`,
          description: `Failed records have been reset to pending for retry.`,
        });
        refetchDashboard();
      } else {
        toast({ title: "Error", description: data.error || "Failed to retry", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, entityType) => {
      setActioningEntity(null);
      setRetryingFailedEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Flag All for Field Sync mutation
  const flagAllFieldSyncMutation = useMutation({
    mutationFn: (entityType: string) => flagAllForFieldSync(entityType),
    onMutate: (entityType) => {
      setFlaggingFieldSyncEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, entityType) => {
      toast({
        title: `Records Flagged: ${entityType}`,
        description: `${data.flaggedCount.toLocaleString()} records flagged for field sync`,
      });
      refetchDashboard();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, entityType) => {
      setFlaggingFieldSyncEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Field Sync mutation
  const fieldSyncMutation = useMutation({
    mutationFn: (entityType: string) => startFieldSync(entityType, maxRecords || undefined),
    onMutate: (entityType) => {
      setFieldSyncingEntities((prev) => new Set(prev).add(entityType));
    },
    onSuccess: (data, entityType) => {
      if (data.success) {
        toast({
          title: `Field Sync Complete: ${entityType}`,
          description: `Processed ${data.processed || 0} records, ${data.succeeded || 0} updated`,
        });
        refetchDashboard();
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to start field sync",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, entityType) => {
      setFieldSyncingEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityType);
        return next;
      });
    },
  });

  // Calculate summary stats from dashboard data
  const stats = dashboardData?.summary || { totalPending: 0, totalFailed: 0, totalDispatched: 0, activeJobs: 0 };
  const activeCount = stats.activeJobs;
  const pausedCount = enabledEntities.filter(e => e.progress?.status === "Paused").length;

  const ensureCanExecute = () => {
    if (!canExecute) {
      toast({
        title: "Access denied",
        description: executeMessage,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view SQL Sync</p>
        </div>
      </div>
    }>
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">SQL Sync Pipeline</h1>
            <p className="text-muted-foreground">
              Synchronize data from Azure SQL to Dataverse
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Auto-refresh</span>
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            </div>
            <Button variant="outline" size="sm" onClick={() => setLocation("/sql-sync/activity")}>
              <Activity className="w-4 h-4 mr-2" />
              Activity Log
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!ensureCanExecute()) return;
                refreshMetadataMutation.mutate();
              }}
              disabled={refreshMetadataMutation.isPending || !canExecute}
              title={!canExecute ? executeMessage : "Refresh all source counts, master counts, and preview scans from database"}
              className={!canExecute ? "opacity-50 cursor-not-allowed" : ""}
            >
              {refreshMetadataMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Refresh Counts
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetchDashboard()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Test Mode Control */}
        <div className="flex items-center p-4 bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Test Mode:</span>
            <select
              value={maxRecords}
              onChange={(e) => setMaxRecords(parseInt(e.target.value, 10))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="0">All Records</option>
              <option value="5">5 records</option>
              <option value="10">10 records</option>
              <option value="25">25 records</option>
              <option value="50">50 records</option>
              <option value="100">100 records</option>
            </select>
            {maxRecords > 0 && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                Limited to {maxRecords}
              </Badge>
            )}
          </div>
        </div>

        {/* Duration Trends Chart */}
        <SyncDurationChart />

        {/* Entity Table */}
        {dashboardLoading && (
          <Skeleton className="h-64 w-full" />
        )}

        {dashboardError && (
          <div className="p-4 bg-red-50 rounded-lg text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>Failed to load dashboard: {(dashboardError as Error).message}</span>
            </div>
          </div>
        )}

        {!dashboardLoading && !dashboardError && enabledEntities.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            No enabled entity configurations found. Enable entities in{" "}
            <code className="bg-muted px-1 rounded">api/src/config/sql-sync/</code>
          </div>
        )}

        {!dashboardLoading && !dashboardError && enabledEntities.length > 0 && (
            <EntityTable
              entities={enabledEntities.map((entity): EntityData => {
                const entityMetadata = metadataByEntity.get(entity.entityType);
                return {
                entity: {
                  entityType: entity.entityType,
                  description: entity.displayName,
                  enabled: entity.enabled,
                },
                // Use metadata sourceCount if available, otherwise fall back to dashboard data
                sourceCount: entityMetadata?.sourceViewCount ?? entity.sourceCount,
                lastScan: entity.lastScan ? {
                  entityType: entity.entityType,
                  timestamp: entity.lastScan.scannedAt,
                  changesFound: entity.lastScan.changesDetected,
                  newRecords: 0,
                  updatedRecords: 0,
                  deletedRecords: 0,
                  durationMs: entity.lastScan.duration,
                } : null,
                progress: entity.progress ? {
                  entityType: entity.entityType,
                  environment: environment,
                  // Always use dashboard data for real-time progress (updated every 5s)
                  // Metadata is only refreshed every 60s and would show stale data during sync
                  total: entity.progress.total,
                  pending: entity.progress.pending,
                  dispatched: entity.progress.dispatched,
                  successful: entity.progress.successful,
                  failed: entity.progress.failed,
                  percentComplete: entity.progress.percentComplete,
                  isActive: entity.progress.isActive,
                  status: entity.progress.status,
                  lastDispatchedAt: entity.progress.lastDispatchedAt,
                  recentErrors: [],
                } : null,
                // Add metadata preview info
                metadata: entityMetadata ? {
                  previewTotalChanges: entityMetadata.previewTotalChanges,
                  previewNewRecords: entityMetadata.previewNewRecords,
                  previewUpdatedRecords: entityMetadata.previewUpdatedRecords,
                  previewDeletedRecords: entityMetadata.previewDeletedRecords,
                  previewScannedAt: entityMetadata.previewScannedAt,
                  largeChangeThreshold: entityMetadata.largeChangeThreshold,
                } : undefined,
                // Field mapping info from dashboard
                hasFieldMappings: entity.hasFieldMappings,
                fieldMappingCount: entity.fieldMappingCount,
                fieldSyncPendingCount: entity.fieldSyncPendingCount,
              };
            })}
            recentHistory={dashboardData?.recentHistory || []}
            maxRecords={maxRecords}
            canExecute={canExecute}
            executeTooltip={executeMessage}
            onScan={(entityType) => {
              if (!ensureCanExecute()) return;
              detectChangesMutation.mutate({ entityType, resetFailedFlag: false });
            }}
            onStart={(entityType) => {
              if (!ensureCanExecute()) return;
              startMutation.mutate({ entityType, env: environment, limit: maxRecords || undefined });
            }}
            onStop={(entityType) => {
              if (!ensureCanExecute()) return;
              stopMutation.mutate({ entityType, env: environment });
            }}
            onResume={(entityType) => {
              if (!ensureCanExecute()) return;
              resumeMutation.mutate({ entityType, env: environment, limit: maxRecords || undefined });
            }}
            onResetAndRescan={(entityType) => {
              if (!ensureCanExecute()) return;
              resetAndRescanMutation.mutate(entityType);
            }}
            onRetryFailed={(entityType) => {
              if (!ensureCanExecute()) return;
              retryFailedMutation.mutate(entityType);
            }}
            onViewErrors={(entityType) => setErrorDrawerEntity(entityType)}
            onViewPendingData={(entityType) => setLocation(`/sql-sync/pending/${entityType}?limit=${maxRecords || 10}`)}
            onFlagAllFieldSync={(entityType) => {
              if (!ensureCanExecute()) return;
              flagAllFieldSyncMutation.mutate(entityType);
            }}
            onFieldSync={(entityType) => {
              if (!ensureCanExecute()) return;
              fieldSyncMutation.mutate(entityType);
            }}
            scanningEntities={scanningEntities}
            startingEntities={startingEntities}
            stoppingEntities={stoppingEntities}
            resumingEntities={resumingEntities}
            resettingEntities={resettingEntities}
            retryingFailedEntities={retryingFailedEntities}
            flaggingFieldSyncEntities={flaggingFieldSyncEntities}
            fieldSyncingEntities={fieldSyncingEntities}
            summaryStats={{
              activeJobs: activeCount,
              pausedJobs: pausedCount,
              totalPending: stats.totalPending,
              totalSynced: stats.totalDispatched,
              totalFailed: stats.totalFailed,
            }}
            onViewAllErrors={() => setLocation("/sql-sync/errors")}
          />
        )}
      </div>

      {/* Failed Records Drawer */}
      {errorDrawerEntity && (
        <FailedRecordsDrawer
          isOpen={!!errorDrawerEntity}
          onClose={() => setErrorDrawerEntity(null)}
          entityType={errorDrawerEntity}
        />
      )}
    </PageWrapper>
    </ViewGuard>
  );
}
